<template>
  <div
    class="relative h-screen w-full bg-[#161616] text-white flex flex-col justify-between pt-[var(--navbar-height)] overflow-hidden"
  >
    <div class="flex-1 overflow-y-auto w-full px-5 md:px-12 xl:px-[263px] box-border relative z-10 pb-10">
      
      <div class="mb-12 mt-10 md:mt-16 w-full flex flex-col items-center">
        <div
          v-for="(faq, index) in faqs"
          :key="index"
          class="faq-item w-full"
        >
          <button
            class="faq-question flex justify-between items-center font-regular"
            @click="toggleAnswer(index)"
          >
            <span class="text-left text-[15px] md:text-[18px]">{{ faq.question }}</span>
            <img 
              src="@/assets/chevron.down.svg"
              :class="{ 'rotate-180': activeIndex === index }"
              alt="Arrow Icon"
              class="w-5 h-3 ml-4 flex-shrink-0 transition-transform duration-300 opacity-70"
            />
          </button>

          <transition
            name="accordion"
            @before-enter="beforeEnter"
            @enter="enter"
            @after-enter="afterEnter"
            @before-leave="beforeLeave"
            @leave="leave"
          >
            <div
              v-if="activeIndex === index"
              class="faq-answer-wrapper overflow-hidden"
            >
              <div class="faq-answer-content font-light">
                {{ faq.answer }}
              </div>
            </div>
          </transition>
        </div>
      </div>

      <div class="w-full max-w-[650px] mx-auto mb-12 px-0">
        <div class="contact-card w-full rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          
          <div class="flex flex-col gap-1.5 flex-1 pr-0 sm:pr-4">
            <h3 class="text-white font-semibold text-[16px] sm:text-[18px]">Still need help?</h3>
            <p class="text-[#A1A1AA] font-light text-[13px] sm:text-[14px] leading-relaxed">
              Can't find what you're looking for? Our support team is ready to help you with any questions or issues.
            </p>
          </div>

          <a
            href="mailto:pearcooperation@gmail.com"
            class="contact-button font-medium whitespace-nowrap flex-shrink-0 w-full sm:w-auto text-center text-[14px] px-6 py-3 rounded-xl transition-all duration-200"
            style="background-color: #4ade80; color: #0f172a;"
          >
            Contact Support
          </a>

        </div>
      </div>

    </div>

    <div
      class="hidden xl:block absolute top-32 bottom-20 w-[1px] bg-[#FFFFFF] opacity-15"
      style="left: 263px; z-index: 0;"
    ></div>
    <div
      class="hidden xl:block absolute top-32 bottom-20 w-[1px] bg-[#FFFFFF] opacity-15"
      style="right: 263px; z-index: 0;"
    ></div>

    <div class="gradient-wrapper">
      <div
        class="fixed bottom-0 left-0 w-[300px] h-[300px]
               bg-gradient-to-tr from-[#12E55A]/60 to-[#AA2FBE]/10
               filter blur-[200px]
               pointer-events-none z-[0]"
      ></div>
    </div>

    <div class="relative z-20 w-full">
      <div
        class="hidden md:block absolute top-0 left-[60px] right-[60px] h-[1px] bg-white opacity-15"
      ></div>
      <footer class="footer font-light">
        © 2026 Sequreio App. All Rights Reserved.
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const faqs = [
  { question: "How do I generate a password?", answer: "Simply open the app, and your secure password will be displayed instantly. If you want a different password, tap the refresh button to generate a new one." },
  { question: "Is my data safe?", answer: "Absolutely. We securely store and never share your data. All your information remains private and secure, even offline." },
  { question: "What is AES-256 encryption and how does it protect my data?", answer: "AES-256 (Advanced Encryption Standard with 256-bit keys) is a military-grade encryption protocol used by governments and financial institutions worldwide. It works by transforming your sensitive data—passwords, usernames, and personal information—into an unreadable cipher using a unique 256-bit encryption key. This means there are 2^256 possible key combinations, making it virtually impossible to crack through brute-force attacks. For example, when you save a password, it's immediately encrypted on your device before being stored or synced. Even if someone intercepts the data, they would only see scrambled characters without the encryption key. This same technology secures online banking, government communications, and classified documents, ensuring your vault remains private and inaccessible to unauthorized parties." },
  { question: "What are Trusted Devices and how do they work? (Premium Feature)", answer: "Trusted Devices is a premium security feature that streamlines your login experience while maintaining maximum protection. When you register a device as trusted, it receives a unique cryptographic identifier that's securely stored in our system. This allows you to bypass Multi-Factor Authentication (MFA) on devices you regularly use—like your personal iPhone—without compromising security. Here's how it works: after your first successful login with MFA verification, the device is automatically registered as trusted. Future logins from that device won't require the additional authentication step. You can manage up to 5 trusted devices simultaneously, and each device stores metadata including its name, platform, location, and last active timestamp. If a device is lost or stolen, you can instantly revoke its trusted status from your account settings, which immediately invalidates all active sessions on that device. This feature is exclusive to Premium subscribers and provides the perfect balance between convenience and enterprise-grade security." },
  { question: "Where are my generated passwords stored?", answer: "Generated passwords are stored in the list at the bottom. You can simply open the dropdown to view your saved passwords, or use the search function to quickly find the password you're looking for." },
  { question: "Can I customize my passwords?", answer: "Yes, you can customize your passwords! Simply adjust the settings to include or exclude specific characters, set the desired length, or add custom preferences to create a password that fits your needs." },
  { question: "How do I enable Multi-Factor Authentication (MFA)?", answer: "To enable Multi-Factor Authentication (MFA), go to your Settings, find the 'Multi Factor Authentication' section, and select Enable MFA. Follow the prompts to set up a second authentication method, like an authenticator app, SMS, or email verification." },
  { question: "What happens if I lose my recovery key?", answer: "If you lose your recovery key, you may not be able to access your account if you get locked out. To prevent this, make sure to store your recovery key in a safe place. If it’s already lost, check if your account offers alternative recovery options, like verifying your identity through email or phone." },
  { question: "Does the app work offline?", answer: "Yes, the app works offline. You can access your passwords locally, as they are securely stored on your device. However, some features like syncing with the cloud or updating data across devices may require an internet connection." },
  { question: "Does Sequreio support Siri and App Shortcuts?", answer: "Yes. Sequreio integrates with Siri and the Apple Shortcuts app so you can perform common actions hands-free or automate them in your own workflows. The following actions are available: \"Generate a password with Sequreio\" — instantly generates a secure password and copies it to your clipboard (you can customise length, symbols, numbers, and uppercase); \"Show my favourite passwords in Sequreio\" — opens your Favourites tab directly; \"Add a new password in Sequreio\" — takes you straight to the Add Password screen; \"Open Sequreio\" — launches the app. You can trigger any of these by asking Siri, adding them to the Shortcuts app, or long-pressing the Sequreio icon on your Home Screen. All Shortcuts are available in English, Polish, German, and Ukrainian. Note: if your vault is locked, you will be asked to verify your PIN before the action completes — your security is never bypassed." },
  { question: "What's included in the Free plan vs Premium?", answer: "Sequreio offers two plans. The Free plan includes core password management — you can store up to 25 passwords and up to 3 folders, generate secure passwords, use biometric authentication, and access your vault offline. The Premium plan unlocks the full Sequreio experience with no limits: unlimited password and folder storage, cross-device synchronisation so your vault is always up to date across all your iPhones, Trusted Devices management for streamlined logins, and priority access to all future features. Premium is available as a monthly or annual subscription — the annual plan offers the best value. You can upgrade at any time from the app. Your existing data is never affected when switching between plans." },
  { question: "How do I cancel my subscription?", answer: "Sequreio subscriptions are billed and managed entirely through Apple. To cancel, open the Settings app on your iPhone, tap your name at the top, then go to Subscriptions. Find Sequreio in the list and tap Cancel Subscription. Your Premium access will remain active until the end of the current billing period — you will not be charged again after cancellation. Alternatively, you can manage your subscription directly through the App Store: open the App Store, tap your profile icon, then tap Subscriptions. If you have any issues or questions about your subscription, please contact us at pearcooperation@gmail.com and we will be happy to help." },
  { question: "Can I sign in with Apple?", answer: "Yes. Sequreio supports Sign in with Apple, which lets you create an account and log in without sharing your email address or creating a separate password. When you use Sign in with Apple, Apple authenticates your identity and we receive only the information needed to create your account. If you choose to hide your email, Apple provides a private relay address that forwards messages from us to your real inbox — you can disable this at any time from your Apple ID settings. Sign in with Apple is available on all supported iOS devices running iOS 15 or later. Your vault data and all Premium features work exactly the same regardless of how you signed up." }
]

const activeIndex = ref(null)

function toggleAnswer(index) {
  activeIndex.value = activeIndex.value === index ? null : index
}

// --- SMOOTH ANIMATION FUNCTIONS ---
const beforeEnter = (el) => {
  el.style.height = '0';
  el.style.opacity = '0';
}

const enter = (el) => {
  el.offsetHeight; 
  el.style.height = el.scrollHeight + 'px';
  el.style.opacity = '1';
}

const afterEnter = (el) => {
  el.style.height = 'auto';
}

const beforeLeave = (el) => {
  el.style.height = el.scrollHeight + 'px';
  el.style.opacity = '1';
}

const leave = (el) => {
  el.offsetHeight;
  el.style.height = '0';
  el.style.opacity = '0';
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Albert+Sans:wght@300;400;500;600;700&display=swap');
</style>

<style scoped>
/* Fonts */
.font-light { font-family: 'Albert Sans', sans-serif; font-weight: 300; }
.font-regular { font-family: 'Albert Sans', sans-serif; font-weight: 400; }

.gradient-wrapper {
  position: fixed;
  bottom: 0; left: 0; right: 0; top: 0;
  z-index: 0;
  pointer-events: none;
}

.faq-item {
  margin-bottom: 12px;
  /* Flex column ensures the answer stays under the button */
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* --- BUTTON STYLING --- */
.faq-question {
  background-color: #262626; 
  color: #E1E2E6;
  font-size: 16px;
  text-align: left;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  position: relative;
  z-index: 2; 
  
  /* Mobile Default (Full Width) */
  width: 100%;
  padding: 18px 24px;
}

.faq-question:hover {
  background-color: #333333;
  border-color: rgba(255, 255, 255, 0.1);
}

/* --- ANSWER WRAPPER --- */
.faq-answer-wrapper {
  background-color: transparent;
  overflow: hidden;
  will-change: height, opacity;
  /* Match Mobile Width by default */
  width: 100%;
}

.faq-answer-content {
  color: #A1A1AA;
  padding: 10px 24px 20px 24px; 
  font-size: 14px;
  line-height: 1.6;
}

.contact-card {
  background-color: #262626;
  border: 1px solid rgba(255, 255, 255, 0.07);
}

.contact-button {
  background-color: #12E55A;
  color: #161616;
  font-weight: 600;
  display: inline-block;
  text-align: center;
}

.contact-button:hover {
  background-color: #0fcc50;
}

/* --- DESKTOP OVERRIDES (min-width 768px) --- */
@media (min-width: 768px) {
  
  /* Use max-width instead of fixed width so it stays fluid on iPad split-view */
  .faq-question {
    max-width: 650px !important;
    width: 100% !important;
    height: 58px !important;
    /* Reset padding to ensure height is respected, flex centers text */
    padding: 0 24px !important; 
    display: flex;
    align-items: center;
  }

  /* Ensure answer box matches the question width fluidly */
  .faq-answer-wrapper {
    max-width: 650px !important;
    width: 100% !important;
  }
}

/* Transition Classes */
.accordion-enter-active,
.accordion-leave-active {
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
              opacity 0.3s ease;
}

.footer {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: rgba(205, 207, 207, 0.9);
  padding: 20px 0;
  background-color: transparent; 
  width: 100%;
  line-height: 24px;
}
</style>